import { VonageContext } from 'context/VonageContext';
import {
  AppointmentChatMessageFrom,
  ContentType,
  FileOutput,
  Hasura_Dnavisit_Test_Statuses_Enum,
  useGetDiagnosticReportsByParticipantsQuery,
  useGetVonageSessionMutation,
} from 'graphql/generated/hasura';
import {
  RoomCredentials,
  RoomPublisherOptions,
  RoomVideoEffects,
  useRoom,
} from 'hooks/vonage/useRoom';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createSearchParams,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { ReactComponent as ChatIcon } from 'assets/icons/appointmentsIcons/chatIconL.svg';
import { ReactComponent as VideoIcon } from 'assets/icons/appointmentsIcons/videoIconL.svg';
import {
  MY_ACCOUNT_PROFILE,
  MY_APPOINTMENTS,
  MY_PATIENTS,
  MY_PATIENTS_BIO_INFO,
  MY_PATIENTS_BIO_QUESTIONNAIRE,
  MY_SKIN_MY_MEDICATION_HISTORY,
} from 'utilities/routes';
import { ReactComponent as VideoCallIcon } from 'assets/icons/videoIcons/video.svg';
import { ReactComponent as VideoBlurIcon } from 'assets/icons/videoIcons/blur.svg';
import { ReactComponent as AudioCallIcon } from 'assets/icons/videoIcons/audio.svg';
import { ReactComponent as ScreenShareIcon } from 'assets/icons/videoIcons/screenShare.svg';
import { ReactComponent as VideoCallDisableIcon } from 'assets/icons/videoIcons/videoDisable.svg';
import { ReactComponent as AudioCallDisableIcon } from 'assets/icons/videoIcons/audioDisable.svg';
import { ReactComponent as ScreenShareDisableIcon } from 'assets/icons/videoIcons/screenShareDisable.svg';
import { ReactComponent as EndCallIcon } from 'assets/icons/videoIcons/endCall.svg';
import { ReactComponent as AudioMobileDisableIcon } from 'assets/icons/videoIcons/mobile/audioDisable.svg';
import { ReactComponent as AttatchMedia } from 'assets/icons/attach.svg';
import { ReactComponent as AudioMobileEnableIcon } from 'assets/icons/videoIcons/mobile/audioEnable.svg';
import { ReactComponent as VideoMobileEnableIcon } from 'assets/icons/videoIcons/mobile/videoEnable.svg';
import { ReactComponent as VideoMobileDisableIcon } from 'assets/icons/videoIcons/mobile/videoDisable.svg';
import { ReactComponent as ScreenShareMobileDisableIcon } from 'assets/icons/videoIcons/mobile/shareScreenDisable.svg';
import { ReactComponent as ScreenShareMobileEnableIcon } from 'assets/icons/videoIcons/mobile/shareScreenEnable.svg';
import { ReactComponent as EndCallMobileIcon } from 'assets/icons/videoIcons/mobile/endCall.svg';
import { ReactComponent as TestKitIcon } from 'assets/icons/testKits/testKit.svg';
import { ReactComponent as Medications } from 'assets/icons/appointmentsIcons/medications.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ChatSolidIcon } from 'assets/icons/ChatSolidIcon';
import { PreworkSolidIcon } from 'assets/icons/PreworkSolidIcon';
import { PreworkOutlineIcon } from 'assets/icons/PreworkOutlineIcon';
import { ChatOutlineIcon } from 'assets/icons/ChatOutlineIcon';
import { MyNotesOutlineIcon } from 'assets/icons/MyNotesOutlineIcon';
import { MyNotesSolidIcon } from 'assets/icons/MyNotesSolidIcon';
import {
  ChangeTextEvent,
  ChatMessage,
  MessageType,
  OnKeyDownEvent,
  Tabs,
  VonageMessageType,
} from '../interfaces';
import { ChatComponent } from '../ChatComponent';
import { useGetPage } from 'hooks/useGetPage';
import {
  KEY_CODE,
  genericActionsIds,
  pageIds,
  PARAM_MODALS_IDENTIFIERS,
  AllowedFormats,
} from 'utilities/constants';
import { Message } from 'hooks/vonage/useSignal';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron-right-white.svg';
import CollapseTab from '../CollapseTab';
import Modal from 'components/modal/modalComponent';
import InputComponent from 'components/inputComponent';
import ButtonComponent from 'components/button/buttonComponent';
import { useScreenSharing } from 'hooks/vonage/useScreenSharing';
import { useIsMobile } from 'hooks/useIsMobile';
import BottomTabs from 'components/tabs/bottomTabs';
import {
  AppointmentTypeEnum,
  useAddMessageToAppointmentMessageListByAppointmentCodexIdMutation,
  useGetAppointmentByCodexAppointmentIdQuery,
  useGetProviderAppointmentByCodexAppointmentIdQuery,
} from 'graphql/generated/remote-schema-hasura';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import { useInAppointmentActions } from 'hooks/useInAppointmentActions';
import { PreworkComponent } from '../PreworkComponent';

import { MyNotesComponent } from '../MyNotesComponents';
import { useAuth, UserData } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { initializeTabsState } from './tabsUtils';
import Alert from 'components/alertComponent';
import {
  RolesTimeStamp,
  TAB_INACTIVE,
  TIME_TO_NOTIFICATION_FROM_ENDING,
} from '../../const/videoRoomConsts';
import { useUploadFileApi } from 'hooks/useUploadFileApi';
import { decodeMediaInChat, encodeMediaInChat } from 'utilities/functions';
import { twMerge } from 'tailwind-merge';
import TestKitsBlock from '../video-room/TestKitsBlock';
import {
  formatFileType,
  mapFileTypeToApiFileType,
} from 'utilities/fileHelpers';
import { MBite } from 'const/validation';
import FieldError from 'components/stepper/field-components/FieldError';
import moment from 'moment/moment';
import AllergensBlock from '../video-room/AllergensBlock';
import { ReactComponent as QuestionnaireIcon } from 'assets/icons/questionnaire.svg';
import { ReactComponent as AllergensIcon } from 'assets/icons/allergies.svg';
import { ReactComponent as AllergensActiveIcon } from 'assets/icons/allergiesBlue.svg';
import { PATIENT_PROFILE_ROUTES } from '../../../my-account/pages/patient/constants';
import { PreviousAppointmentSummaryBlock } from '../video-room/PreviousAppointmentSummaryBlock';
import { useGetFileFromStorage } from '../../../../hooks/useGetFileFromStorage';

const MAX_FILE_SIZE = 100 * MBite;

export const groupChatMessages = (messages: ChatMessage[]) => {
  const messageList: ChatMessage[] = [];
  for (const message of messages) {
    const senderType = message.senderType;

    if (messageList.length && message.type !== MessageType.MEDIA) {
      const lastIndex = messageList.length - 1;
      const lastMessage = messageList[lastIndex];
      if (
        lastMessage.message &&
        message.message &&
        lastMessage.senderType === senderType &&
        lastMessage.type !== MessageType.MEDIA
      ) {
        messageList[lastIndex] = {
          ...lastMessage,
          messageGroup: lastMessage.messageGroup
            ? [...lastMessage.messageGroup, message]
            : [lastMessage, message],
        };
        continue;
      }
    }

    messageList.push(message);
  }
  return messageList;
};

const getUserTimeStamp = (role: Roles | undefined): number => {
  if (!role) {
    return 0;
  }
  return RolesTimeStamp[role];
};

const formatMessage = (
  message: Message[],
  currentRole: Roles,
  loggedUser: UserData | null,
): ChatMessage[] => {
  return message.map<ChatMessage>((message, key) => {
    const currentRoleAsSenderType =
      currentRole === Roles.PATIENT ? Roles.PATIENT : Roles.PROVIDER;
    return {
      id: key.toString(),
      message: message.data,
      senderType: message.isSentByMe
        ? currentRoleAsSenderType
        : currentRoleAsSenderType === Roles.PATIENT
        ? Roles.PROVIDER
        : Roles.PATIENT,
      senderPicture: message.isSentByMe
        ? loggedUser?.profilePicture
        : undefined,
      senderName: message.isSentByMe ? loggedUser?.displayName : undefined,
      type: message.imageData ? MessageType.MEDIA : MessageType.MESSAGE,
      mediaURL: message.imageData?.mediaUrl,
    };
  });
};

export const VideoRoom: React.FC = () => {
  const { user: loggedUser } = useAuth();
  const { user } = useContext(VonageContext);
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useIsMobile();
  const [credentials, setCredentials] = useState<Record<string, string> | null>(
    null,
  );
  const { data: appoinmentsLocale, loading: appointmentsLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.MY_APPOINTMENTS,
  });
  const { data: genericAction, loading: genericActionLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [genericActionsIds.CLOSE],
    });
  const [hasAudio, setHasAudio] = useState(user?.defaultSettings.publishAudio);
  const [hasVideo, setHasVideo] = useState(user?.defaultSettings.publishVideo);
  const [error, setError] = useState<Error | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const appointmentType = location.state?.appointmentType;
  const [getCredentials] = useGetVonageSessionMutation({
    fetchPolicy: 'no-cache',
  });
  const { createCall, room, listOfMessages, sendSignal, toggleBlur } =
    useRoom();
  const { isScreenSharing, startScreenSharing, stopScreenSharing } =
    useScreenSharing({ room });

  const { getUrlFileFromVonageStorage, fetchFileFromStorage } =
    useGetFileFromStorage();

  const roomContainer = useRef<HTMLDivElement | null>(null);
  const [notes, setNotes] = useState('');
  const [isBlurEnabled, setIsBlurEnabled] = useState<boolean>(
    user?.videoEffects?.backgroundBlur || false,
  );
  const [isBlurButtonDisabled, setIsBlurButtonDisabled] =
    useState<boolean>(false);
  const [errorAttachFileMessages, setErrorAttachFileMessages] = useState<
    string | null
  >(null);

  const handleToggleBlur = useCallback(async () => {
    setIsBlurButtonDisabled(true);
    try {
      await toggleBlur(!isBlurEnabled);
      setIsBlurEnabled((blur) => !blur);
    } catch (e) {
      console.error('Failed to toggle blur', e);
    } finally {
      setIsBlurButtonDisabled(false);
    }
  }, [isBlurEnabled, toggleBlur]);

  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.COMPLETE_MY_VISIT_SUMMARY_MODAL,
  });
  const firstRoomRender = useRef(false);
  const remainingTime = useRef<number>();
  const remainingTimeTimer = useRef<NodeJS.Timeout>();
  const [remainingTimeText, setRemainingTimeText] = useState('');
  const inputButtonRef = useRef<HTMLInputElement | null>(null);
  useEffect(
    () => () => {
      if (remainingTimeTimer.current !== undefined) {
        clearTimeout(remainingTimeTimer.current);
      }
    },
    [],
  );

  const parseRemainingTime = useCallback(() => {
    const seconds = Math.floor((remainingTime.current ?? 0) / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) {
      return setRemainingTimeText(`${seconds} ${appoinmentsLocale.seconds}`);
    }
    return setRemainingTimeText(`${minutes} ${appoinmentsLocale?.minutes}`);
  }, [appoinmentsLocale]);

  const useGetAppointmentDetails =
    loggedUser?.role === Roles.PATIENT
      ? useGetAppointmentByCodexAppointmentIdQuery
      : useGetProviderAppointmentByCodexAppointmentIdQuery;

  const { data: appointmentData, loading: appointmentDataLoading } =
    useGetAppointmentDetails({
      variables: { appointmentCodexId: location.state.room },
      skip: !location.state.room,
    });

  const patientId = useMemo(
    () =>
      appointmentData?.getFHIRAppointmentByCodexId?.appointment
        ?.codexPatientId ?? '',
    [appointmentData],
  );

  const patientName = useMemo(() => {
    const { patientFirstName, patientLastName } =
      appointmentData?.getFHIRAppointmentByCodexId?.appointment || {};

    return `${patientFirstName} ${patientLastName}`;
  }, [appointmentData]);

  const { data: tests } = useGetDiagnosticReportsByParticipantsQuery({
    skip: !loggedUser?.uuid || !patientId || loggedUser.role !== Roles.PROVIDER,
    fetchPolicy: 'cache-and-network',
    variables: {
      providerCodexId: loggedUser?.uuid as string,
      patientCodexId: patientId as string,
      state: [
        Hasura_Dnavisit_Test_Statuses_Enum.AppointmentScheduled,
        Hasura_Dnavisit_Test_Statuses_Enum.ResultsWithProvider,
      ],
    },
  });

  const { leftEvent, saveNotes } = useInAppointmentActions(
    location.state.room,
    appointmentData?.getFHIRAppointmentByCodexId?.appointment?.encounterId,
    notes,
  );
  const [addMessages] =
    useAddMessageToAppointmentMessageListByAppointmentCodexIdMutation({});

  const [text, setText] = useState('');
  const [showTimeReminder, setShowTimeReminder] = useState(true);
  const [showCallEndingNotification, setShowCallEndingNotification] =
    useState(false);

  const [active, setActive] = useState(Tabs.Chat);
  const handleOnToggle = (tab: number) => {
    if (tab === TAB_INACTIVE) return;
    setActive((prev) => (prev === tab ? -1 : tab));
  };

  const sendMessage = useCallback(
    (text: string, type: string = VonageMessageType.TEXT) => {
      if (type !== VonageMessageType.TEXT) {
        const decodedData = decodeMediaInChat(text);
        const fileType = formatFileType(decodedData?.contentType as string);
        addMessages({
          variables: {
            appointmentCodexId: location.state.room,
            messages: [
              {
                contentType: fileType as ContentType,
                fileName: decodedData?.fileName || null,
                fileId: decodedData?.mediaId || null,
                size: decodedData?.fileSize
                  ? +decodedData?.fileSize
                  : undefined,
                mediaUrl:
                  (decodedData as unknown as Record<string, string>)
                    ?.mediaUrl ?? undefined,
                from: loggedUser?.role?.toUpperCase() as AppointmentChatMessageFrom,
                date: moment().toISOString(),
              },
            ],
          },
        });
      } else {
        addMessages({
          variables: {
            appointmentCodexId: location.state.room,
            messages: [
              {
                message: text,
                from: loggedUser?.role?.toUpperCase() as AppointmentChatMessageFrom,
                date: moment().toISOString(),
              },
            ],
          },
        });
      }
      if (room) {
        sendSignal(text, type);
        setText('');
      }
    },
    [addMessages, location.state.room, loggedUser?.role, room, sendSignal],
  );

  const changeText = (event: ChangeTextEvent) => {
    setText(event.target.value);
  };
  const onKeyDown = (e: OnKeyDownEvent) => {
    if (e.keyCode === KEY_CODE.ENTER && !!text) {
      e.preventDefault();
      sendMessage(text);
      setText('');
    }
  };

  useEffect(() => {
    setChatMessages(
      groupChatMessages(
        formatMessage(
          listOfMessages,
          loggedUser?.role ?? Roles.PATIENT,
          loggedUser,
        ),
      ),
    );
  }, [listOfMessages, loggedUser, loggedUser?.role]);

  useEffect(() => {
    const getVonageCredentials = async () => {
      try {
        const { data } = await getCredentials({
          variables: {
            room: location.state.room,
          },
        });
        const vonageCredentials = data?.GetVonageSession;
        setCredentials({
          apikey: vonageCredentials?.apiKey ?? '',
          sessionId: vonageCredentials?.sessionId ?? '',
          token: vonageCredentials?.token ?? '',
        });
      } catch (error) {
        console.error(error);
        setError(error as Error);
        console.log(`Error getting vonage credentials: ${error}`);
      }
    };
    getVonageCredentials();
  }, [getCredentials, location.state.room]);

  const { uploadVonageFile: uploadFile, isUploading: isMediaSaving } =
    useUploadFileApi();

  const sendUploadedFileToChat = useCallback(
    async (fileData: FileOutput) => {
      const { fileId, contentType, fileName, size } = fileData;

      if (!fileId || !contentType) return;

      const storageUrl = await getUrlFileFromVonageStorage(
        fileId,
        location.state.room,
      );
      if (!storageUrl) return;

      const res = await fetch(storageUrl);
      if (!res.ok) throw new Error('Failed to download file from storage');

      const name = fileName ? fileName.split('.')[0] : 'test';
      const message = encodeMediaInChat(
        fileId,
        storageUrl,
        contentType,
        name,
        size,
      );

      sendMessage(message, mapFileTypeToApiFileType[contentType]);
    },
    [getUrlFileFromVonageStorage, location.state.room, sendMessage],
  );

  const shareTestKit = useCallback(
    async (fileData: FileOutput) => {
      const { fileId, contentType, fileName } = fileData;

      if (!fileId || !contentType) return;
      const storageUrl = await fetchFileFromStorage(fileId, patientId);
      if (!storageUrl) return;

      handleOnToggle(Tabs.Chat);

      const res = await fetch(storageUrl);
      if (!res.ok) throw new Error('Failed to download file from storage');

      const blob = await res.blob();

      const safeFileName =
        fileName || (contentType === ContentType.Pdf ? 'test-kit.pdf' : 'file');

      const file = new File([blob], safeFileName, {
        type: contentType === ContentType.Pdf ? 'application/pdf' : blob.type,
      });

      const result = await uploadFile(file, location.state.room);
      const uploadedFile = result?.file;

      if (
        (result?.errors && result.errors.length) ||
        !uploadedFile?.fileId ||
        !uploadedFile?.url ||
        !uploadedFile?.contentType
      ) {
        throw new Error('Upload failed');
      }

      const name = uploadedFile.fileName
        ? uploadedFile.fileName.split('.')[0]
        : safeFileName.split('.')[0] ?? 'file';

      const message = encodeMediaInChat(
        uploadedFile.fileId,
        uploadedFile.url,
        uploadedFile.contentType,
        name,
      );
      sendMessage(message, mapFileTypeToApiFileType[uploadedFile.contentType]);
    },
    [
      fetchFileFromStorage,
      location.state.room,
      patientId,
      sendMessage,
      uploadFile,
    ],
  );

  const handleImageUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!AllowedFormats.includes(file.type)) {
        return setErrorAttachFileMessages(
          'This file type is not supported. Please upload Images, PDF, Excel, Docs',
        );
      }

      if (file.size >= MAX_FILE_SIZE) {
        return setErrorAttachFileMessages('Maximum file size is 100 MB');
      }

      const result = await uploadFile(file, location.state.room);
      const uploadedFile = result?.file;

      if (
        (result?.errors && result.errors.length) ||
        !uploadedFile?.fileId ||
        !uploadedFile?.url
      ) {
        return;
      }

      setErrorAttachFileMessages(null);

      sendUploadedFileToChat({
        fileId: uploadedFile.fileId,
        url: uploadedFile.url,
        contentType: uploadedFile.contentType,
        fileName: uploadedFile.fileName,
        size: uploadedFile.size,
      });
    },
    [
      location.state.room,
      uploadFile,
      sendUploadedFileToChat,
      setErrorAttachFileMessages,
    ],
  );

  useEffect(() => {
    setTimeout(() => setErrorAttachFileMessages(null), 5000);
  }, [errorAttachFileMessages]);

  useEffect(() => {
    if (
      appointmentType &&
      appointmentType === AppointmentTypeEnum.Chat &&
      room &&
      room.camera
    ) {
      const camera = room.camera;
      camera.disableAudio();
      camera.disableVideo();
    }
  }, [appointmentType, room]);

  const toggleAudio = () => {
    if (room && room.camera) {
      const camera = room.camera;
      const isAudioEnabled = camera.isAudioEnabled();
      if (isAudioEnabled) {
        camera.disableAudio();
        setHasAudio(false);
      } else {
        camera.enableAudio();
        setHasAudio(true);
      }
    }
  };

  const toggleVideo = () => {
    if (room && room.camera) {
      const { camera } = room;
      const isVideoEnabled = camera.isVideoEnabled();
      if (isVideoEnabled) {
        camera.disableVideo();
        setHasVideo(false);
      } else {
        camera.enableVideo();
        setHasVideo(true);
      }
    }
  };

  const endCall = useCallback(() => {
    leftEvent()
      .then(async () => {
        const stream: MediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
      })
      .catch((error) => console.error(error));
    if (room) {
      room.leave();
    }
    if (loggedUser?.role === Roles.PROVIDER) {
      navigate(
        {
          pathname: MY_APPOINTMENTS,
          search: createSearchParams({
            [PARAM_MODALS_IDENTIFIERS.FILL_IN_SUMMARY_MODAL]: 'true',
          }).toString(),
        },
        { state: { appointmentId: location.state.room } },
      );
    } else if (loggedUser?.role === Roles.PATIENT) {
      navigate(MY_APPOINTMENTS, {
        state: {
          appointmentComplete: true,
        },
      });
    }
  }, [leftEvent, location.state.room, loggedUser?.role, navigate, room]);

  const handleTimeReminder = useCallback(
    (appointmentEndDate: Date) => {
      remainingTimeTimer.current = undefined;
      remainingTime.current =
        appointmentEndDate.getTime() -
        Date.now() +
        getUserTimeStamp(loggedUser?.role);
      if (remainingTime.current <= TIME_TO_NOTIFICATION_FROM_ENDING) {
        setShowCallEndingNotification(true);
      }
      if (remainingTime.current <= 0) {
        return endCall();
      }
      parseRemainingTime();
      remainingTimeTimer.current = setTimeout(
        () => handleTimeReminder(appointmentEndDate),
        1000,
      );
    },
    [endCall, loggedUser?.role, parseRemainingTime],
  );

  const endTime = appointmentData?.getFHIRAppointmentByCodexId.appointment?.end;
  useEffect(() => {
    if (endTime) {
      if (remainingTimeTimer.current) {
        clearTimeout(remainingTimeTimer.current);
      }
      handleTimeReminder(new Date(endTime));
    }
  }, [endTime, handleTimeReminder]);

  const [tabs, setTabs] = useState(() =>
    initializeTabsState(appointmentType, loggedUser),
  );

  const handleTabSelected = (index: number) => {
    const updatedTabs = tabs.map((tab, i) => ({
      ...tab,
      isSelected: i === index,
    }));
    setTabs(updatedTabs);
  };

  const getIcon = () => {
    switch (appointmentType) {
      case AppointmentTypeEnum.Video:
        return <VideoIcon height="20" width="20" />;
      case AppointmentTypeEnum.Chat:
      default:
        return <ChatIcon height="20" width="20" />;
    }
  };

  const getTitle = () => {
    switch (appointmentType) {
      case AppointmentTypeEnum.Video:
        return appoinmentsLocale?.video?.deets;
      case AppointmentTypeEnum.Chat:
      default:
        return appoinmentsLocale?.chat?.deets;
    }
  };

  useEffect(() => {
    if (credentials && roomContainer.current && !firstRoomRender.current) {
      const videoEffects = user?.videoEffects || {
        backgroundBlur: false,
        videoSourceId: user?.videoEffects?.videoSourceId ?? '',
      };
      const defaultSettings = {
        publishAudio: true,
        publishVideo: true,
        ...(user?.defaultSettings || {}),
      };

      createCall(
        credentials as unknown as RoomCredentials,
        roomContainer.current as unknown as string,
        user?.userName as string,
        videoEffects as RoomVideoEffects,
        defaultSettings as RoomPublisherOptions,
      );
      firstRoomRender.current = true;
    }
  }, [
    createCall,
    credentials,
    user,
    loading,
    appointmentsLoading,
    appoinmentsLocale,
    genericActionLoading,
    appointmentDataLoading,
  ]);

  if (
    error ||
    !locale ||
    loading ||
    !appoinmentsLocale ||
    appointmentsLoading ||
    genericActionLoading ||
    !genericAction ||
    appointmentDataLoading
  ) {
    return null;
  }

  return (
    <Modal
      isOpen={true}
      title={getTitle()}
      icon={getIcon()}
      onClose={endCall}
      modalDesktopWidth="desktop:max-w-full"
      modalDesktopHeight="desktop:h-full"
      disableOverflow={true}
      contentPaddingX="0"
      contentPaddingY="0"
      hFull
      customContentContainerClass="overflow-auto max-w-[100vw]"
    >
      <div className="flex flex-col h-full desktop:p-4 gap-2 overflow-auto">
        {showTimeReminder && (
          <Alert
            type="info"
            text={`${appoinmentsLocale.appointmentEnding} ${remainingTimeText}`}
            onClose={() => setShowTimeReminder(false)}
            isNotification
          />
        )}
        {showCallEndingNotification && (
          <Alert
            type="warning"
            text={`${appoinmentsLocale.appointmentEnding} ${remainingTimeText}`}
            isNotification
            hideCloseButton
          />
        )}
        <div className="flex flex-row grow gap-4 justify-center items-center overflow-auto">
          <div
            className={`flex flex-col w-full justify-center items-center h-full ${
              appointmentType === AppointmentTypeEnum.Chat && !isMobile
                ? 'hidden desktop:hidden'
                : ''
            }`}
          >
            {appointmentType &&
            appointmentType === AppointmentTypeEnum.Video ? (
              <div
                id="roomContainer"
                className={`relative h-[85vh] top-0 w-screen desktop:w-full desktop:h-full ${
                  isMobile ? (tabs[0].isSelected ? 'flex' : 'hidden') : ''
                }`}
                ref={roomContainer}
              >
                <div
                  id="screenSharingContainer"
                  className="absolute top-0 left-0 z-10"
                >
                  {isScreenSharing && (
                    <div className="absolute top-0 left-0 right-0 bottom-0 z-10 flex justify-center items-center text-h4 font-medium text-neutral-50">
                      {appoinmentsLocale?.screenSharing}
                    </div>
                  )}
                </div>
                {isMobile && (
                  <div className="absolute top-[10%] left-4 z-20 flex flex-col gap-[30px]">
                    {hasVideo ? (
                      <VideoMobileEnableIcon
                        className="cursor-pointer"
                        onClick={toggleVideo}
                      />
                    ) : (
                      <VideoMobileDisableIcon
                        className="cursor-pointer"
                        onClick={toggleVideo}
                      />
                    )}
                    {hasAudio ? (
                      <AudioMobileEnableIcon
                        className="cursor-pointer"
                        onClick={toggleAudio}
                      />
                    ) : (
                      <AudioMobileDisableIcon
                        className="cursor-pointer"
                        onClick={toggleAudio}
                      />
                    )}
                    {isScreenSharing ? (
                      <ScreenShareMobileDisableIcon
                        className="cursor-pointer"
                        onClick={stopScreenSharing}
                      />
                    ) : (
                      <ScreenShareMobileEnableIcon
                        className="cursor-pointer"
                        onClick={startScreenSharing}
                      />
                    )}
                    <EndCallMobileIcon
                      className="cursor-pointer"
                      onClick={endCall}
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div id="roomContainer" ref={roomContainer} />
                <div id="screenSharingContainer" />
              </>
            )}
            {isMobile && (
              <div
                className={`flex flex-col w-full h-[85vh] p-5 pt-10 bg-white rounded-10 overflow-auto ${
                  isMobile && appointmentType === AppointmentTypeEnum.Video
                    ? tabs[1].isSelected
                      ? 'flex'
                      : 'hidden'
                    : tabs[1].isSelected
                    ? 'flex'
                    : 'hidden'
                }`}
              >
                <PreworkComponent
                  prework={appointmentData}
                  symptomsLocation={
                    appointmentData?.getFHIRAppointmentByCodexId.appointment
                      ?.symptomsLocation
                  }
                  locale={locale}
                />
              </div>
            )}
            {isMobile && loggedUser?.role === Roles.PROVIDER && (
              <div
                className={`flex flex-col w-full h-[85vh] p-5 pt-10 bg-white rounded-10 overflow-hidden ${
                  isMobile && appointmentType === AppointmentTypeEnum.Video
                    ? tabs[3].isSelected
                      ? 'flex'
                      : 'hidden'
                    : tabs[2].isSelected
                    ? 'flex'
                    : 'hidden'
                }`}
              >
                <MyNotesComponent
                  locale={appoinmentsLocale.myNotes}
                  value={notes}
                  onChange={setNotes}
                  saveNotes={saveNotes}
                />
              </div>
            )}
            {isMobile && (
              <div
                className={`flex flex-col w-full h-[85vh] p-2 bg-white rounded-10 overflow-hidden ${
                  isMobile && appointmentType === AppointmentTypeEnum.Video
                    ? tabs[2].isSelected
                      ? 'flex'
                      : 'hidden'
                    : tabs[0].isSelected
                    ? 'flex'
                    : 'hidden'
                }`}
              >
                <div className="h-[80vh] overflow-y-auto">
                  <ChatComponent
                    loggedUser={loggedUser}
                    locale={locale.chatMessage}
                    messages={chatMessages}
                  />
                </div>
                <div className="flex items-center justify-center w-full gap-2">
                  <div className="flex w-full">
                    <InputComponent
                      testID="chatMessage-input"
                      type="text"
                      name="chatMessage"
                      onChange={changeText}
                      value={text}
                      onKeyDown={onKeyDown}
                      noMarginBottom
                      maxLengthValue={250}
                    />
                  </div>
                  <div>
                    <ButtonComponent
                      disabled={!text}
                      paddingX="px-[8px] desktop:px-[2px]"
                      paddingY="py-[5px] desktop:py-[5px]"
                      Icon={ChevronRightIcon}
                      testID={'chevron-right-icon'}
                      onClick={() => {
                        sendMessage(text);
                        setText('');
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {isMobile && (
              <div className="flex w-full h-[8vh]">
                <BottomTabs tabs={tabs} onTabSelected={handleTabSelected} />
              </div>
            )}
            {!isMobile &&
              appointmentType &&
              appointmentType === AppointmentTypeEnum.Video && (
                <div className="flex w-full justify-center items-center gap-[30px] bg-white p-[30px] rounded-b-10">
                  {hasVideo ? (
                    <VideoCallIcon
                      className="cursor-pointer"
                      onClick={toggleVideo}
                    />
                  ) : (
                    <VideoCallDisableIcon
                      className="cursor-pointer"
                      onClick={toggleVideo}
                    />
                  )}
                  {hasAudio ? (
                    <AudioCallIcon
                      className="cursor-pointer"
                      onClick={toggleAudio}
                    />
                  ) : (
                    <AudioCallDisableIcon
                      className="cursor-pointer"
                      onClick={toggleAudio}
                    />
                  )}
                  {isScreenSharing ? (
                    <ScreenShareDisableIcon
                      className="cursor-pointer"
                      onClick={stopScreenSharing}
                    />
                  ) : (
                    <ScreenShareIcon
                      className="cursor-pointer"
                      onClick={startScreenSharing}
                    />
                  )}
                  <VideoBlurIcon
                    onClick={() =>
                      isBlurButtonDisabled ? () => ({}) : handleToggleBlur()
                    }
                    className={twMerge(
                      'cursor-pointer border-2 rounded-full border-transparent',
                      isBlurEnabled && 'border-clc-blue',
                      isBlurButtonDisabled && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  <EndCallIcon className="cursor-pointer" onClick={endCall} />
                </div>
              )}
          </div>
          {!isMobile && (
            <>
              {appointmentType === AppointmentTypeEnum.Chat && (
                <div className="flex flex-col w-5/12 h-full bg-white rounded-10 overflow-hidden">
                  <CollapseTab
                    active
                    activeIcon={PreworkSolidIcon}
                    icon={PreworkOutlineIcon}
                    label={locale.preworkTab}
                    onClick={() => handleOnToggle(TAB_INACTIVE)}
                    standalone
                    hiddeArrow={appointmentType === AppointmentTypeEnum.Chat}
                  >
                    <PreworkComponent
                      prework={appointmentData}
                      symptomsLocation={
                        appointmentData?.getFHIRAppointmentByCodexId.appointment
                          ?.symptomsLocation
                      }
                      locale={locale}
                    />
                  </CollapseTab>
                </div>
              )}
              <div
                className={`flex flex-col ${
                  appointmentType === AppointmentTypeEnum.Video
                    ? 'w-5/12'
                    : 'w-full'
                } h-full bg-white rounded-10 overflow-hidden`}
              >
                {appointmentType === AppointmentTypeEnum.Video && (
                  <CollapseTab
                    active={
                      active === Tabs.Prework ||
                      appointmentType === AppointmentTypeEnum.Chat
                    }
                    activeIcon={PreworkSolidIcon}
                    icon={PreworkOutlineIcon}
                    label={locale.preworkTab}
                    onClick={() => handleOnToggle(Tabs.Prework)}
                  >
                    <PreworkComponent
                      prework={appointmentData}
                      symptomsLocation={
                        appointmentData?.getFHIRAppointmentByCodexId.appointment
                          ?.symptomsLocation
                      }
                      locale={locale}
                    />
                  </CollapseTab>
                )}
                <CollapseTab
                  active={false}
                  activeElement={
                    <Link
                      target="_blank"
                      to={
                        loggedUser?.role === Roles.PROVIDER
                          ? `${MY_PATIENTS}/${patientId}/${MY_PATIENTS_BIO_INFO}/medication-history`
                          : `${MY_SKIN_MY_MEDICATION_HISTORY}`
                      }
                      className="underline text-sm font-semibold uppercase text-clc-blue"
                    >
                      View medication history
                    </Link>
                  }
                  activeIcon={Medications}
                  icon={Medications}
                  label={'Medication history'}
                  onClick={() => ({})}
                  hiddeArrow
                >
                  {null}
                </CollapseTab>
                <CollapseTab
                  active={
                    active === Tabs.Chat ||
                    appointmentType === AppointmentTypeEnum.Chat
                  }
                  activeIcon={ChatSolidIcon}
                  borderBottom={active === Tabs.Chat}
                  icon={ChatOutlineIcon}
                  label={locale.chatTab}
                  standalone={appointmentType === AppointmentTypeEnum.Chat}
                  onClick={() =>
                    handleOnToggle(
                      appointmentType === AppointmentTypeEnum.Video
                        ? Tabs.Chat
                        : TAB_INACTIVE,
                    )
                  }
                  hiddeArrow={appointmentType === AppointmentTypeEnum.Chat}
                >
                  <div
                    className={`h-full flex flex-col gap-2 justify-between ${
                      isMediaSaving ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="overflow-y-auto h-full">
                      <ChatComponent
                        loggedUser={loggedUser}
                        locale={locale.chatMessage}
                        messages={chatMessages}
                      />
                    </div>
                    <div className="flex items-center justify-center w-full pt-1 gap-2">
                      <input
                        type="file"
                        accept=".jpg, .jpeg, .png, .pdf, .xls, .xlsx, .doc, .docx, .txt"
                        onChange={handleImageUpload}
                        ref={inputButtonRef}
                        className="hidden"
                      />
                      <div
                        className="cursor-pointer flex items-center justify-center rounded-full"
                        onClick={() => inputButtonRef.current?.click()}
                      >
                        <AttatchMedia className="w-6 h-6 fill-clc-blue" />
                      </div>
                      <div className="flex flex-row gap-2 justify-between bg-white grow-0 text-base border input-border__gray rounded-md placeholder-[#666] text-gray-700 focus-within:outline-none focus-within:border-blue-400 overflow-hidden w-full">
                        <input
                          data-testid="chatMessage-input"
                          type="text"
                          name="chatMessage"
                          className="text-med-gray-3 w-full h-12 font-semibold text-sm outline-none border-0 focus:outline-none p-3"
                          onChange={changeText}
                          value={text}
                          onKeyDown={onKeyDown}
                          maxLength={250}
                        />
                      </div>
                      <ButtonComponent
                        borderRadius="rounded-[5px]"
                        disabled={!text}
                        testID={'chevron-right-icon'}
                        paddingX="px-[8px] desktop:px-[2px]"
                        paddingY="py-[5px] desktop:py-[5px]"
                        className="mr-4"
                        Icon={ChevronRightIcon}
                        onClick={() => {
                          sendMessage(text);
                          setText('');
                        }}
                      />
                    </div>
                    {errorAttachFileMessages && (
                      <FieldError className="whitespace-pre-wrap">
                        {errorAttachFileMessages}
                      </FieldError>
                    )}
                  </div>
                </CollapseTab>
                {appointmentType === AppointmentTypeEnum.Video &&
                  loggedUser?.role === Roles.PROVIDER && (
                    <CollapseTab
                      active={
                        active === Tabs.MyNotes ||
                        appointmentType === AppointmentTypeEnum.Chat
                      }
                      activeIcon={MyNotesSolidIcon}
                      icon={MyNotesOutlineIcon}
                      label={locale.myNotes}
                      onClick={() => handleOnToggle(Tabs.MyNotes)}
                    >
                      <MyNotesComponent
                        locale={appoinmentsLocale.myNotes}
                        value={notes}
                        onChange={setNotes}
                        saveNotes={saveNotes}
                      />
                    </CollapseTab>
                  )}
                {loggedUser?.role === Roles.PROVIDER &&
                  !!(
                    tests?.getDiagnosticReportsByParticipants
                      ?.diagnosticReports ?? []
                  )?.length && (
                    <CollapseTab
                      active={active === Tabs.TestKit}
                      activeIcon={TestKitIcon}
                      icon={TestKitIcon}
                      label={'Test kit results'}
                      onClick={() => handleOnToggle(Tabs.TestKit)}
                    >
                      <TestKitsBlock
                        tests={
                          tests?.getDiagnosticReportsByParticipants
                            ?.diagnosticReports || []
                        }
                        patientId={patientId}
                        sendPdfToChat={shareTestKit}
                      />
                    </CollapseTab>
                  )}

                <CollapseTab
                  active={active === Tabs.PastAppointments}
                  activeIcon={CalendarIcon}
                  icon={() => <CalendarIcon className="fill-med-gray-3" />}
                  label={'Past appointment summaries'}
                  onClick={() => handleOnToggle(Tabs.PastAppointments)}
                >
                  <PreviousAppointmentSummaryBlock patientCodexId={patientId} />
                </CollapseTab>

                <CollapseTab
                  active={false}
                  activeElement={
                    <Link
                      target="_blank"
                      to={
                        loggedUser?.role === Roles.PROVIDER
                          ? `${MY_PATIENTS}/${patientId}/${MY_PATIENTS_BIO_INFO}/${MY_PATIENTS_BIO_QUESTIONNAIRE}`
                          : `${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.HEALTH_QUESTIONNAIRE}`
                      }
                      className="underline text-sm font-semibold uppercase text-clc-blue"
                    >
                      View questionnaire
                    </Link>
                  }
                  activeIcon={QuestionnaireIcon}
                  icon={QuestionnaireIcon}
                  label={'Questionnaire'}
                  onClick={() => ({})}
                  hiddeArrow
                >
                  {null}
                </CollapseTab>

                <CollapseTab
                  active={active === Tabs.Allergens}
                  activeIcon={AllergensActiveIcon}
                  icon={AllergensIcon}
                  label={'Allergies'}
                  onClick={() => handleOnToggle(Tabs.Allergens)}
                >
                  <AllergensBlock
                    patientName={patientName}
                    patientCodexId={patientId}
                  />
                </CollapseTab>
              </div>
              {appointmentType === AppointmentTypeEnum.Chat &&
                loggedUser?.role === Roles.PROVIDER && (
                  <div className="flex flex-col w-5/12 h-full bg-white rounded-10 overflow-hidden">
                    <CollapseTab
                      active
                      activeIcon={MyNotesSolidIcon}
                      icon={MyNotesOutlineIcon}
                      label={locale.myNotes}
                      onClick={() => handleOnToggle(TAB_INACTIVE)}
                      hiddeArrow={appointmentType === AppointmentTypeEnum.Chat}
                      inverted
                    >
                      <MyNotesComponent
                        locale={appoinmentsLocale.myNotes}
                        value={notes}
                        onChange={setNotes}
                        saveNotes={saveNotes}
                      />
                    </CollapseTab>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
